(() => {
  if (window.PuzzlePupPdpNavigation) return;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const track = (eventName, payload) => window.PuzzlePupAnalytics?.track(eventName, payload);

  function setupGallery(root) {
    if (!root || root.dataset.ppReady) return;
    root.dataset.ppReady = 'true';
    const slides = [...root.querySelectorAll('[data-pp-gallery-slide]')];
    const thumbs = [...root.querySelectorAll('[data-pp-gallery-thumb]')];
    const counter = root.querySelector('[data-pp-gallery-counter]');
    const previous = root.querySelector('[data-pp-gallery-prev]');
    const next = root.querySelector('[data-pp-gallery-next]');
    const imageAdvancers = [...root.querySelectorAll('[data-pp-gallery-image-next]')];
    let index = 0;
    let requestedIndex = 0;
    let requestId = 0;

    const primeNextImage = () => {
      const image = slides[index + 1]?.querySelector('img');
      if (image) {
        image.fetchPriority = 'low';
        image.loading = 'eager';
      }
    };

    const commit = (nextIndex, focusThumb = false) => {
      index = Math.min(Math.max(nextIndex, 0), slides.length - 1);
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = String(index + 1) + ' of ' + String(slides.length);
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
      if (focusThumb) thumbs[index]?.focus();
      root.removeAttribute('aria-busy');
      primeNextImage();
      document.dispatchEvent(new CustomEvent('pp:gallery-changed'));
    };

    const show = async (nextIndex, focusThumb = false) => {
      requestedIndex = Math.min(Math.max(nextIndex, 0), slides.length - 1);
      const targetIndex = requestedIndex;
      const currentRequest = ++requestId;
      const image = slides[targetIndex]?.querySelector('img');
      if (image && (!image.complete || image.naturalWidth === 0)) {
        image.loading = 'eager';
        root.setAttribute('aria-busy', 'true');
        if (counter) counter.textContent = 'Loading photo…';
        try { await image.decode(); } catch (error) {
          if (currentRequest !== requestId) return;
          if (!image.naturalWidth) {
            root.removeAttribute('aria-busy');
            requestedIndex = index;
            if (counter) counter.textContent = 'Photo could not load. Tap again.';
            return;
          }
        }
      }
      if (currentRequest !== requestId) return;
      commit(targetIndex, focusThumb);
    };

    previous?.addEventListener('click', () => show(requestedIndex - 1));
    next?.addEventListener('click', () => show(requestedIndex + 1));
    imageAdvancers.forEach(control => {
      const advance = () => show(requestedIndex >= slides.length - 1 ? 0 : requestedIndex + 1);
      control.addEventListener('click', advance);
      control.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        advance();
      });
    });
    thumbs.forEach((thumb, thumbIndex) => thumb.addEventListener('click', () => show(thumbIndex)));
    root.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(requestedIndex - 1, true); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(requestedIndex + 1, true); }
    });
    commit(0);
  }

  function stickyOffset() {
    const candidates = [
      document.querySelector('sticky-header'),
      document.querySelector('.shopify-section-header-sticky'),
      document.querySelector('[data-pp-campaign-banner]')
    ].filter(Boolean);
    return candidates.reduce((height, item) => height + item.getBoundingClientRect().height, 0);
  }

  function setupIndex(nav) {
    if (!nav || nav.dataset.ppReady) return;
    nav.dataset.ppReady = 'true';
    const links = [...nav.querySelectorAll('[data-pp-index-link]')];
    const select = nav.querySelector('[data-pp-index-select]');
    const targets = links.map(link => document.querySelector(link.getAttribute('href'))).filter(Boolean);
    const updateOffset = () => {
      const offset = Math.round(stickyOffset());
      document.documentElement.style.setProperty('--pp-index-top', String(offset) + 'px');
      document.documentElement.style.setProperty('--pp-sticky-offset', String(offset + nav.getBoundingClientRect().height) + 'px');
      targets.forEach(target => { target.style.scrollMarginTop = String(offset + nav.getBoundingClientRect().height + 14) + 'px'; });
    };
    updateOffset();
    let scheduledFrame = 0;
    const updateActive = () => {
      scheduledFrame = 0;
      const activationLine = stickyOffset() + nav.getBoundingClientRect().height + 20;
      const positions = targets.map(target => ({ target, top: target.getBoundingClientRect().top }))
        .sort((a, b) => a.top - b.top);
      const passed = positions.filter(item => item.top <= activationLine);
      const current = passed[passed.length - 1]?.target || positions[0]?.target;
      if (!current) return;
      links.forEach(link => {
        if (link.getAttribute('href') === '#' + current.id) {
          link.setAttribute('aria-current', 'location');
          if (select) select.value = '#' + current.id;
        } else link.removeAttribute('aria-current');
      });
    };
    const scheduleActive = () => {
      if (!scheduledFrame) scheduledFrame = requestAnimationFrame(updateActive);
    };
    window.addEventListener('scroll', scheduleActive, { passive: true });
    window.addEventListener('resize', () => { updateOffset(); scheduleActive(); }, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => { updateOffset(); scheduleActive(); });
      observer.observe(nav);
      targets.forEach(target => observer.observe(target));
    }
    scheduleActive();

    links.forEach(link => link.addEventListener('click', event => {
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      track('pp_pdp_index_click', { section_id: target.id, control_type: 'link' });
      target.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' });
      if (reducedMotion()) updateActive();
      history.replaceState(null, '', link.getAttribute('href'));
    }));
    select?.addEventListener('change', () => {
      const target = document.querySelector(select.value);
      if (!target) return;
      track('pp_pdp_index_click', { section_id: target.id, control_type: 'select' });
      target.scrollIntoView({ behavior: reducedMotion() ? 'instant' : 'smooth', block: 'start' });
      if (reducedMotion()) updateActive();
      history.replaceState(null, '', select.value);
    });
  }

  function setupStickyBuy() {
    const buybox = document.querySelector('[data-pp-buybox]');
    const sticky = document.querySelector('[data-pp-sticky-buy]');
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (!buybox || !sticky || sticky.dataset.ppReady) return;
    sticky.dataset.ppReady = 'true';
    let buyboxVisible = true;
    let footerVisible = false;
    const render = () => {
      sticky.hidden = buyboxVisible || footerVisible || document.body.classList.contains('pp-v3-drawer-open');
    };
    new IntersectionObserver(entries => { buyboxVisible = entries[0]?.isIntersecting ?? true; render(); }, { threshold: .08 }).observe(buybox);
    if (footer) new IntersectionObserver(entries => { footerVisible = entries[0]?.isIntersecting ?? false; render(); }, { threshold: .01 }).observe(footer);
    document.addEventListener('pp:drawer-state', render);
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-pp-gallery]').forEach(setupGallery);
    scope.querySelectorAll('[data-pp-index]').forEach(setupIndex);
    setupStickyBuy();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.PuzzlePupPdpNavigation = Object.freeze({ init });
})();

